import {
  OktonErrorState,
  OktonLoadingState,
  OktonPageHeader,
  useOptionalOktonAuth,
} from "@okton/platform-ui";
import React from "react";
import {
  Badge,
  Box,
  Button,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { AiOffer, BranchContext, Catalog, Subscription } from "./types";
import * as api from "./aiPlansApi";
import { AiPlanCheckout } from "./AiPlanCheckout";
import { AiPlanAdditional } from "./AiPlanAdditional";
import { AiPlanTextPanel } from "./AiPlanTextPanel";
export const AiPlanCatalog: React.FC<{
  token: string;
  system: string;
  context: BranchContext;
}> = ({ token, system, context }) => {
  const auth = useOptionalOktonAuth();
  const [data, setData] = React.useState<Catalog | null>(null);
  const [selection, setSelection] = React.useState<{
    offer: AiOffer;
    key: string;
  } | null>(null);
  const [contract, setContract] = React.useState<Subscription | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [reload, setReload] = React.useState(0);
  const generation = React.useRef(0);
  const submitting = React.useRef(false);
  React.useEffect(() => {
    const controller = new AbortController();
    const current = ++generation.current;
    setSelection(null);
    setContract(null);
    setData(null);
    setError("");
    setBusy(false);
    submitting.current = false;
    setLoading(true);
    api
      .catalog(token, system, context, controller.signal)
      .then((result) => {
        if (current === generation.current) setData(result);
      })
      .catch((reason) => {
        if (!controller.signal.aborted)
          setError(reason.message || "Não foi possível carregar os planos.");
      })
      .finally(() => {
        if (current === generation.current) setLoading(false);
      });
    return () => {
      generation.current = current + 1;
      controller.abort();
    };
    // Only IDs identify the confirmed branch; a display-name refresh does not reset acceptance.
  }, [token, system, context.companyId, context.branchId, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const confirm = async () => {
    if (!selection || submitting.current || !data?.canSubscribe) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    const current = generation.current;
    try {
      const result = await api.subscribe(token, system, context, {
        offerId: selection.offer.id,
        expectedRevision: selection.offer.revision,
        termsVersion: selection.offer.termsVersion,
        idempotencyKey: selection.key,
        accepted: true,
      });
      if (current !== generation.current) return;
      setContract(result);
      setSelection(null);
      // Refresh official entitlement; execution still obeys its independent technical gates.
      void api
        .request(
          "/v1/me/ai-plan-license?system=" + encodeURIComponent(system),
          token,
        )
        .catch(() => undefined);
    } catch (reason) {
      if (current === generation.current)
        setError(
          reason instanceof Error
            ? reason.message
            : "Não foi possível confirmar. Tente novamente.",
        );
    } finally {
      if (current === generation.current) {
        submitting.current = false;
        setBusy(false);
      }
    }
  };
  if (loading)
    return <OktonLoadingState title="Carregando planos..." minH="240px" />;
  if (!data)
    return (
      <OktonErrorState
        title="Não foi possível carregar os planos"
        description={error || "Catálogo indisponível."}
        onRetry={() => setReload((value) => value + 1)}
        minH="240px"
      />
    );
  const current = contract || data.current;
  return (
    <Stack spacing="4">
      <OktonPageHeader
        title="Planos de IA"
        subtitle={context.branchName || "Filial " + context.branchId}
      />
      <Text>Cobrança automática ainda não habilitada.</Text>
      {current && (
        <Box borderWidth="1px" borderRadius="lg" p="4">
          <Badge colorScheme="green">
            {contract ? "Plano contratado" : "Plano atual"}
          </Badge>
          <Text>
            {current.snapshot.name} · {api.price(current.snapshot)}{" "}
            {api.period(current.snapshot)}
          </Text>
          <Text>Vinculado à filial no cadastro oficial.</Text>
        </Box>
      )}
      {current && (
        <AiPlanAdditional
          key={[
            token,
            system,
            context.companyId,
            context.branchId,
            current.id,
            current.extraPermissionEnabled,
            data.license?.extraPermissionEnabled,
          ].join(":")}
          token={token}
          system={system}
          context={context}
          authorized={
            current.extraPermissionEnabled === true &&
            data.license?.extraPermissionEnabled === true
          }
        />
      )}
      {current && auth?.user?.id && (
        <AiPlanTextPanel
          subject={auth.user.id}
          token={token}
          system={system}
          context={context}
          contractId={current.id}
        />
      )}
      {!current && data.license && (
        <Text>
          Plano atual: {data.license.planName}. A filial já possui acesso ao
          módulo IA.
        </Text>
      )}
      {!data.canSubscribe && (
        <Text>
          A contratação deve ser confirmada por um administrador da empresa.
        </Text>
      )}
      {!data.offers.length && (
        <Text>
          Os planos estão em preparação. Entre em contato com a Okton para
          conhecer as opções.
        </Text>
      )}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4">
        {data.offers.map((offer) => (
          <Box key={offer.id} borderWidth="1px" borderRadius="lg" p="5">
            <Stack spacing="3">
              <Heading size="md">{offer.name}</Heading>
              <Text>{offer.description}</Text>
              <Text fontSize="xl" fontWeight="bold">
                {api.price(offer)}{" "}
                <Text as="span" fontSize="sm">
                  {api.period(offer)}
                </Text>
              </Text>
              <Text>
                {offer.quota} {offer.unit}
              </Text>
              <Text>{offer.performanceProfile}</Text>
              {offer.features.map((feature, index) => (
                <Text key={index}>• {feature}</Text>
              ))}
              <Button
                colorScheme="blue"
                isDisabled={
                  !data.canSubscribe || Boolean(current || data.license) || busy
                }
                onClick={() => {
                  setError("");
                  setSelection({ offer, key: api.confirmationKey() });
                }}
              >
                Selecionar {offer.name}
              </Button>
            </Stack>
          </Box>
        ))}
      </SimpleGrid>
      {selection && (
        <AiPlanCheckout
          key={selection.key + selection.offer.id}
          offer={selection.offer}
          context={context}
          busy={busy}
          error={error}
          onConfirm={confirm}
          onCancel={() => {
            setSelection(null);
            setError("");
          }}
        />
      )}
    </Stack>
  );
};
